import React from 'react';
import { Link } from 'react-router-dom';
import { Accessibility, Type, Pause, Keyboard, Eye, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import { useT } from '../i18n';

const C = {
  en: {
    eyebrow: 'ACCESSIBILITY STATEMENT',
    title: 'Designed for everyone.',
    intro: 'Kitxens is committed to ensuring digital accessibility for people with disabilities. We continually improve the user experience for everyone and apply the relevant accessibility standards.',
    standard: 'Conformance status',
    standardBody: 'This website aims to conform to the Web Content Accessibility Guidelines (WCAG) 2.1, Level AA. These guidelines explain how to make web content more accessible to people with a wide range of disabilities.',
    features: 'Accessibility features on this site',
    list: [
      { i: Accessibility, t: 'UserWay accessibility widget', b: 'An assistive panel (bottom-left icon) with contrast, text spacing, dyslexia-friendly fonts, screen-reader support and more.' },
      { i: Type, t: 'Text size controls', b: 'A / A / A controls in the footer let you increase base text size across the whole site.' },
      { i: Pause, t: 'Pausable motion', b: 'The live news ticker includes a visible pause/play button, and animations respect your reduced-motion preferences.' },
      { i: Keyboard, t: 'Keyboard navigation', b: 'Skip-to-content link, visible focus outlines and a logical tab order on every page.' },
      { i: Eye, t: 'Readable by design', b: 'Minimum 15px body text, high-contrast palette and descriptive alt text on meaningful images.' },
    ],
    feedback: 'Feedback',
    feedbackBody: 'We welcome your feedback on the accessibility of kitxens.com. If you encounter any barrier, please let us know and we will do our best to fix it promptly:',
    contactCta: 'Contact us',
    updated: 'This statement was last reviewed in June 2026.',
  },
  es: {
    eyebrow: 'DECLARACIÓN DE ACCESIBILIDAD',
    title: 'Diseñado para todos.',
    intro: 'Kitxens está comprometido con garantizar la accesibilidad digital para personas con discapacidad. Mejoramos continuamente la experiencia de todos los usuarios aplicando los estándares de accesibilidad relevantes.',
    standard: 'Estado de conformidad',
    standardBody: 'Este sitio web aspira a cumplir las Pautas de Accesibilidad para el Contenido Web (WCAG) 2.1, Nivel AA. Estas pautas explican cómo hacer el contenido web más accesible para personas con una amplia variedad de discapacidades.',
    features: 'Funciones de accesibilidad de este sitio',
    list: [
      { i: Accessibility, t: 'Widget de accesibilidad UserWay', b: 'Un panel asistivo (icono inferior izquierdo) con contraste, espaciado de texto, fuentes para dislexia, soporte de lector de pantalla y más.' },
      { i: Type, t: 'Control de tamaño de texto', b: 'Los controles A / A / A del pie de página permiten aumentar el tamaño base del texto en todo el sitio.' },
      { i: Pause, t: 'Movimiento pausable', b: 'El ticker de noticias incluye un botón visible de pausa/reproducción, y las animaciones respetan tus preferencias de movimiento reducido.' },
      { i: Keyboard, t: 'Navegación por teclado', b: 'Enlace de salto al contenido, focos visibles y orden de tabulación lógico en cada página.' },
      { i: Eye, t: 'Legible por diseño', b: 'Texto base mínimo de 15px, paleta de alto contraste y textos alternativos descriptivos en imágenes relevantes.' },
    ],
    feedback: 'Comentarios',
    feedbackBody: 'Agradecemos tus comentarios sobre la accesibilidad de kitxens.com. Si encuentras alguna barrera, háznoslo saber y haremos lo posible por corregirla rápidamente:',
    contactCta: 'Contáctanos',
    updated: 'Esta declaración se revisó por última vez en junio de 2026.',
  },
};

export default function AccessibilityPage() {
  const { lang } = useT();
  const c = C[lang] || C.en;
  return (
    <div className="pt-28 md:pt-32" data-testid="accessibility-page">
      <SEO
        path="/accessibility"
        title={lang === 'es' ? 'Declaración de Accesibilidad | Kitxens' : 'Accessibility Statement | Kitxens'}
        description={c.intro}
      />
      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <p className="inline-flex items-center gap-2 text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">
            <Accessibility className="w-4 h-4" /> {c.eyebrow}
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold text-neutral-900 leading-[1.05] tracking-[-0.02em]">{c.title}</h1>
          <p className="mt-6 text-neutral-600 text-base sm:text-lg leading-relaxed">{c.intro}</p>

          <h2 className="mt-12 text-lg font-bold text-neutral-900">{c.standard}</h2>
          <p className="mt-3 text-neutral-600 text-[15px] leading-relaxed">{c.standardBody}</p>

          <h2 className="mt-12 text-lg font-bold text-neutral-900">{c.features}</h2>
          <ul className="mt-5 space-y-4">
            {c.list.map((f) => (
              <li key={f.t} className="flex items-start gap-4 rounded-2xl border border-neutral-200 bg-white p-5">
                <span className="w-10 h-10 shrink-0 rounded-xl bg-orange-50 flex items-center justify-center">
                  <f.i className="w-5 h-5 text-[#F77F11]" />
                </span>
                <span>
                  <p className="font-semibold text-neutral-900 text-[15px]">{f.t}</p>
                  <p className="mt-1 text-neutral-600 text-[14px] leading-relaxed">{f.b}</p>
                </span>
              </li>
            ))}
          </ul>

          <h2 className="mt-12 text-lg font-bold text-neutral-900">{c.feedback}</h2>
          <p className="mt-3 text-neutral-600 text-[15px] leading-relaxed">{c.feedbackBody}</p>
          <Link
            to="/contact"
            className="mt-5 inline-flex items-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] text-white h-11 px-6 rounded-full text-sm font-semibold transition-colors"
            data-testid="accessibility-contact-cta"
          >
            <Mail className="w-4 h-4" /> {c.contactCta}
          </Link>
          <p className="mt-10 text-[13px] text-neutral-400">{c.updated}</p>
        </div>
      </section>
    </div>
  );
}
